import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MediaPlayerInterface } from '../media-player.interface'
import { MediaPlayerControllerComponent } from '../media-player-controller/media-player-controller.component'
import { Call, CallsService, hasUnlabeledSpeakers, MetricType } from '../calls.service'
import { ActivatedRoute, Router } from '@angular/router'
import { DownloadsService } from '../downloads.service'
import { MetricsService } from '../metrics.service'
import { LoggerService } from '../logger.service'

@Component({
  selector: 'app-audio-player',
  standalone: true,
  imports: [MediaPlayerControllerComponent],
  templateUrl: './audio-player.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './audio-player.component.scss',
})
export class AudioPlayerComponent implements MediaPlayerInterface {
  @Input()
  call!: Call

  @Output()
  jumpToNextSpeaker = new EventEmitter<number>()

  @Output()
  updateMediaTime = new EventEmitter<number>()

  @ViewChild('player', { read: ElementRef })
  player: ElementRef | undefined = undefined

  downloadsService = inject(DownloadsService)
  metricsService = inject(MetricsService)
  logger = inject(LoggerService)
  router = inject(Router)
  route = inject(ActivatedRoute)

  get isPlaying(): boolean {
    if (this.player?.nativeElement === undefined) {
      return false
    }
    return !this.player?.nativeElement?.paused
  }

  get allowDownload(): boolean {
    return this.call.allowMediaDownload
  }

  get displaySpeakerLabelingModeButton(): boolean {
    return hasUnlabeledSpeakers(this.call)
  }

  play(): void {
    const audioPlayerElement = this.player?.nativeElement ?? null
    if (audioPlayerElement === null) {
      this.logger.warn('Audio player element is not available, cannot play audio.')
      return
    }
    audioPlayerElement.play().catch((error: any) => {
      this.logger.error('Error playing audio:', error)
    })
  }

  pause(): void {
    const audioPlayerElement = this.player?.nativeElement ?? null
    if (audioPlayerElement === null) {
      this.logger.warn('Audio player element is not available, cannot pause audio.')
      return
    }
    audioPlayerElement.pause()
    this.updateMediaTime.emit()
  }

  seek(time: number): void {
    if (this.player?.nativeElement === undefined) {
      return
    }
    this.player.nativeElement.currentTime = time
    this.updateMediaTime.emit()
  }

  changeVolume(volume: number): void {
    if (this.player?.nativeElement === undefined) {
      return
    }
    this.player.nativeElement.volume = volume
  }

  getTime(): number {
    return this.player?.nativeElement?.currentTime
  }

  getDuration(): number {
    return this.player?.nativeElement?.duration
  }

  changePlaybackRate(rate: number): void {
    if (this.player?.nativeElement === undefined) {
      return
    }
    this.player.nativeElement.playbackRate = rate
  }

  handleJumpToNextSpeaker() {
    if (this.player?.nativeElement === undefined) {
      return
    }
    this.jumpToNextSpeaker.emit(this.player.nativeElement.currentTime)
  }

  download() {
    this.downloadsService.downloadFromUrl(this.call.audioUrl!)
    this.metricsService.sendMetric(this.call.id, MetricType.DOWNLOAD_AUDIO_RECORDING)
  }
}
